import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logger from "../../../utils/LoggerFrontend";
import { getMinimumClicks } from "../../../utils/Helpers";
import Actions from "../../../Actions";
import { withRouter } from "react-router-dom";
import AdAssistantStore from "../AdAssistantStore";
import { withRefluxStore } from "../../../Stores";
import AssistantBudgetCalculator from "./assistant-budget-calculator/AssistantBudgetCalculator";
import { AdAssistantLayout, CartNavigationWrapper, FlexWrapper } from "../AdAssistant.styled";
import { ActionsWrapper, Header } from "../edition-modal/EditionModal.styled";
import { EmptyButton } from "../overview/AdAssistantOverview.styled";
import { ArrowLeftIcon } from "../../../Components/Icons";
import Cart from "../cart/Cart";
import SaveNavigation from "../save-navigation/SaveNavigation";
import {
  FixedColumn,
  ModalWrapper,
  ScrollableContent
} from "../all-variants-modal/AllVariantsModal.styled";
import BookingSwitcher from "../../Campaign/Campaign__BookingSwitcher";
import AdAssistantCreatePackages from "../create-packages/AdAssistantCreatePackages";
import { useSaveCampaign } from "../hooks/useSaveCampaign";
import { ActionsAdAssistant } from "../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import { Spinner } from "../../../Components/Upload/Upload.styled";
import { checkCampaignStatus } from "../utils";

const AdAssistantBudgetCheck = ({ match, history, organization, usedAds, campaignData }) => {
  const { t } = useTranslation();
  const { campaignId } = match.params;
  const saveCampaign = useSaveCampaign(campaignId);

  const [minClicksPackage, setMinClicksPackage] = useState(null);
  const [bookingStyle, setBookingStyle] = useState("new");
  const [campaignBudgetData, setCampaignBudgetData] = useState({});
  const [campaignIsLoading, setCampaignIsLoading] = useState(true);

  useEffect(() => {
    if (organization?.id && campaignId) {
      setCampaignIsLoading(true);
      Actions.loadDraftCampaign(campaignId, organization?.id);
      const campaignDataLoaded = Actions.loadDraftCampaign.completed.listen(
        ({ organizationId }) => {
          if (organizationId) {
            Actions.loadOrganizationOfCampaign(organizationId);
          }
          setCampaignIsLoading(false);
          campaignDataLoaded();
        }
      );
    }
  }, [organization, campaignId]);

  useEffect(() => {
    if (campaignData) {
      const searchParams = new URLSearchParams(window.location.search);
      const currentBudgetCheck = searchParams.get("budgetCheck");
      const shouldBeBudgetCheck = !campaignData?.predictions;

      if (currentBudgetCheck === "true" && !shouldBeBudgetCheck) {
        history.replace(`/assistant/budget-check/${campaignId}?budgetCheck=false`);
      } else if (currentBudgetCheck === "false" && shouldBeBudgetCheck) {
        history.replace(`/assistant/budget-check/${campaignId}?budgetCheck=true`);
      }
    }
  }, [campaignData, campaignId, history]);

  const handleNewCampaignData = (data) => {
    // Only update if data has actually changed
    if (JSON.stringify(campaignBudgetData) !== JSON.stringify(data)) {
      setCampaignBudgetData(data);
      ActionsAdAssistant.setManualCampaignData(data);
    }
  };

  const handleSaveCampaign = useCallback(
    async (data) => {
      try {
        const newCampaignData = {
          ...data,
          step: "budget-check",
          bookingStyle,
          ...campaignBudgetData
        };

        await saveCampaign(newCampaignData);
      } catch (error) {
        Actions.addNotification(t("Saved failed"), "error");
      }
    },
    [bookingStyle, campaignBudgetData, saveCampaign, t]
  );

  const handleBackToOverview = () => {
    const newStatus = checkCampaignStatus(campaignData);
    handleSaveCampaign({ status: newStatus });
    history.push(`/assistant/overview/${campaignId}`);
  };

  /**
   * Calculates the minimum clicks for the campaign
   * @param {number} budget Lifetime budget for the campaign
   * @param {object[]} selectedChannels Selected channels for the campaign
   * @returns {number} Minimum clicks for the campaign
   */
  const calcMinClicks = (budget = 400, selectedChannels = ["youtube"]) => {
    logger.debug("calcMinClicks", "called", {
      budget,
      selectedChannels,
      minClicksPackage
    });

    if (minClicksPackage) {
      return minClicksPackage;
    }

    return getMinimumClicks(budget, selectedChannels);
  };

  const onShowPreview = (channel) => {
    // TODO: Implement preview functionality if needed
    logger.debug("onShowPreview", "called", { channel });
  };

  //***** START OF TEMPORARY SOLUTION *****
  const handleSwitchBookingStyle = useCallback(
    (style) => {
      if (bookingStyle !== style) {
        setBookingStyle(style);
        ActionsAdAssistant.setManualCampaignData({ bookingStyle: style });
        Actions.switchBookingStyle(style);
      }
    },
    [bookingStyle]
  );

  useEffect(() => {
    if (campaignData && campaignData.bookingStyle !== bookingStyle) {
      handleSwitchBookingStyle(campaignData.bookingStyle || "new");
    }
  }, [campaignData, bookingStyle, handleSwitchBookingStyle]);
  //***** END OF TEMPORARY SOLUTION *****

  return (
    <AdAssistantLayout>
      <Header>
        <ActionsWrapper>
          <EmptyButton onClick={handleBackToOverview}>
            <ArrowLeftIcon />
          </EmptyButton>
        </ActionsWrapper>
      </Header>

      <ModalWrapper>
        <ScrollableContent>
          {campaignIsLoading ? (
            <FlexWrapper
              gap={11}
              align={"flex-start"}
              wrap={"wrap"}
              justify={"flex-start"}
              width={"100%"}
            >
              <Spinner />
            </FlexWrapper>
          ) : (
            <FlexWrapper
              gap={11}
              align={"flex-start"}
              wrap={"wrap"}
              justify={"flex-start"}
              width={"100%"}
            >
              {organization?.isAgency && (
                <BookingSwitcher
                  bookingStyle={bookingStyle}
                  onSwitch={(style) => handleSwitchBookingStyle(style)}
                  isAssistant={true}
                />
              )}

              {bookingStyle === "new" && (
                <AssistantBudgetCalculator
                  onShowPreview={onShowPreview}
                  calcMinClicks={calcMinClicks}
                  isBudgetCheck={!campaignBudgetData?.predictions}
                  setCampaignBudgetData={handleNewCampaignData}
                />
              )}

              {bookingStyle === "old" && (
                <AdAssistantCreatePackages setCampaignBudgetData={handleNewCampaignData} />
              )}
            </FlexWrapper>
          )}
        </ScrollableContent>
        <FixedColumn>
          <CartNavigationWrapper paddingRight={"0"}>
            <Cart selectedItems={usedAds.length} />
            <SaveNavigation
              selectedItems={usedAds.length}
              emitSaveCampaign={(data) => handleSaveCampaign(data)}
              navigationUrl={`/assistant/checkout/${campaignId}`}
              navigationText={"Checkout"}
            />
          </CartNavigationWrapper>
        </FixedColumn>
      </ModalWrapper>
    </AdAssistantLayout>
  );
};

export default withRouter(
  withRefluxStore(
    AdAssistantBudgetCheck,
    [AdAssistantStore],
    ["organization", "usedAds", "campaignData"]
  )
);
